'use client';

import { useState, useCallback, useEffect } from 'react';
import { calculateROI } from '@/lib/calculations';
import { DEFAULT_VALUES } from '@/types/calculator';
import { DEFAULT_FORM_VALUES } from '@/lib/env';
import { useSettings } from '@/contexts/SettingsContext';
import { getSuggestedTurnoverRateForSimpleCalculator, getExactTurnoverRateForAdvancedCalculator, getCountryAverageTurnoverRate } from '@/lib/turnover-rates';
import type { CalculatorInputs, PricingConfig, CalculationResults } from '@/types/calculator';
import type { SupportedCurrency } from '@/lib/env';
import type { ShareableState } from '@/lib/share-utils';

// Storage keys for persistence
// Note: Wizard step and results are no longer persisted - they reset on each page load
// Share links handle their own state via encrypted URL parameters
const STORAGE_KEYS = {
  CALCULATOR_INPUTS: 'accessnow-roi-calculator-inputs',
} as const;

// Storage helper functions
function saveToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
}

function loadFromStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
    return defaultValue;
  }
}

export interface CalculatorState {
  inputs: CalculatorInputs;
  pricing: PricingConfig;
  results: CalculationResults | null;
  isCalculating: boolean;
  currency: SupportedCurrency;
  wizardStep: number;
}

export interface CalculatorActions {
  setInputs: (inputs: CalculatorInputs) => void;
  setPricing: (pricing: PricingConfig) => void;
  updateInput: <K extends keyof CalculatorInputs>(field: K, value: CalculatorInputs[K]) => void;
  updatePricing: <K extends keyof PricingConfig>(field: K, value: PricingConfig[K]) => void;
  setCurrency: (currency: SupportedCurrency) => void;
  calculate: () => void;
  reset: (mode?: 'simple' | 'detailed' | 'wizard') => void;
  loadDefaults: () => void;
  resetPricing: () => void;
  switchToSimpleMode: () => void;
  switchToDetailedMode: () => void;
  setWizardStep: (step: number) => void;
  resetWizard: () => void;
  loadSharedState: (shareData: ShareableState, targetWizardStep?: number) => void;
}

// Helper function to get country-aware defaults
const getCountryAwareDefaults = (country?: string): CalculatorInputs => {
  // Use exact country average (not rounded) for more precise calculations
  const countryAverageRate = getCountryAverageTurnoverRate(country);
  
  return {
    ...DEFAULT_FORM_VALUES,
    staffTurnoverRate: countryAverageRate,
    country,
    industry: undefined, // Start with no industry selected, use country average
  };
};

export function useCalculator(
  options: { disableLocalStorage?: boolean; initialCurrency?: SupportedCurrency } = {}
): CalculatorState & CalculatorActions {
  const { disableLocalStorage = false, initialCurrency } = options;
  const settings = useSettings();
  
  const [inputs, setInputs] = useState<CalculatorInputs>(() => {
    if (disableLocalStorage) {
      return DEFAULT_VALUES;
    }
    return loadFromStorage(STORAGE_KEYS.CALCULATOR_INPUTS, DEFAULT_VALUES);
  });
  
  // Results are always calculated fresh - never persisted to localStorage
  const [results, setResults] = useState<CalculationResults | null>(null);
  
  const [isCalculating, setIsCalculating] = useState(false);
  // Wizard step always starts at 0 - not persisted to localStorage
  // Share links override this via loadSharedState()
  const [wizardStep, setWizardStep] = useState(0);
  const [isResetting, setIsResetting] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Use persistent settings for pricing and currency
  const pricing = settings.pricing;
  const currency = initialCurrency || settings.currency;

  // Persist inputs to localStorage (unless disabled for share links)
  useEffect(() => {
    if (disableLocalStorage) {
      return;
    }
    saveToStorage(STORAGE_KEYS.CALCULATOR_INPUTS, inputs);
  }, [inputs, disableLocalStorage]);

  const updateInput = useCallback(<K extends keyof CalculatorInputs>(
    field: K, 
    value: CalculatorInputs[K]
  ) => {
    if (field === 'staffTurnoverRate') {
      console.log('⚙️ useCalculator.updateInput:', field, 'to', value);
    }
    setHasUserInteracted(true);
    setInputs(prev => ({ ...prev, [field]: value }));
  }, []);

  const updatePricing = useCallback(<K extends keyof PricingConfig>(
    field: K, 
    value: PricingConfig[K]
  ) => {
    settings.updatePricing(field, value);
  }, [settings]);

  const calculate = useCallback(() => {
    setHasUserInteracted(true);
    setIsCalculating(true);
    try {
      const calculatedResults = calculateROI(inputs, pricing);
      setResults(calculatedResults);
    } catch (error) {
      console.error('Calculation error:', error);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  }, [inputs, pricing]);

  // Auto-recalculate when inputs or pricing change (if user has interacted and results exist)
  useEffect(() => {
    if (hasUserInteracted && results && !isCalculating && !isResetting) {
      const timeoutId = setTimeout(() => {
        calculate();
      }, 300); // 300ms debounce for responsive feel
      
      return () => clearTimeout(timeoutId);
    }
  }, [inputs, pricing, calculate, hasUserInteracted, results, isCalculating, isResetting]);

  const loadDefaults = useCallback(() => {
    // Set resetting flag to prevent auto-calculation
    setIsResetting(true);
    
    // Load configured default values
    setInputs(DEFAULT_VALUES);
    
    // Clear resetting flag after a short delay to allow state to settle
    setTimeout(() => {
      setIsResetting(false);
    }, 100);
  }, []);

  const reset = useCallback((mode?: 'simple' | 'detailed' | 'wizard') => {
    // Set resetting flag to prevent auto-calculation
    setIsResetting(true);
    
    // Clear results and state
    setResults(null);
    setIsCalculating(false);
    setWizardStep(0);
    setHasUserInteracted(false);
    
    // Use current inputs to get country, avoiding dependency issues
    setInputs(currentInputs => {
      // Apply mode-specific defaults
      if (mode === 'simple') {
        // Get country-aware defaults
        const countryDefaults = getCountryAwareDefaults(currentInputs.country);
        
        // Calculate monthly defaults for Simple mode using country-aware turnover rate
        const annualAccessRequests = countryDefaults.cardholderDatabaseSize * countryDefaults.accessRequestsPerCardholder;
        const monthlyAccessDefault = Math.round(annualAccessRequests / 12);
        
        const annualCredentialRequests = (countryDefaults.cardholderDatabaseSize * countryDefaults.cardReplacementPercentage / 100) + 
                                         (countryDefaults.cardholderDatabaseSize * countryDefaults.staffTurnoverRate / 100);
        const monthlyCredentialDefault = Math.round(annualCredentialRequests / 12);

        return {
          ...countryDefaults,
          // Set monthly defaults for Simple mode
          credentialRequestsPerMonth: monthlyCredentialDefault,
          accessRequestsPerMonth: monthlyAccessDefault,
          // Clear database fields for Simple mode
          cardholderDatabaseSize: 0,
          accessRequestsPerCardholder: 0,
        };
      } else {
        // For detailed/wizard modes, use database-based defaults with no industry initially
        const countryDefaults = getCountryAwareDefaults(currentInputs.country);
        
        return {
          ...countryDefaults,
          // Clear monthly inputs for Detailed/Wizard modes
          credentialRequestsPerMonth: 0,
          accessRequestsPerMonth: 0,
        };
      }
    });
    
    // Clear resetting flag after a short delay to allow state to settle
    setTimeout(() => {
      setIsResetting(false);
    }, 100);
    
    // Note: Don't reset pricing settings as they should persist
  }, []);

  const resetWizard = useCallback(() => {
    setWizardStep(0);
  }, []);

  const switchToSimpleMode = useCallback(() => {
    setInputs(prevInputs => {
      // If all inputs are currently empty/zero, load defaults
      const isEmpty = prevInputs.cardholderDatabaseSize === 0 && 
                     prevInputs.costPerEmployee === 0 && 
                     prevInputs.credentialRequestsPerMonth === 0 &&
                     prevInputs.accessRequestsPerMonth === 0;
      
      if (isEmpty) {
        // Get country-aware defaults for Simple mode
        const countryDefaults = getCountryAwareDefaults(prevInputs.country);
        
        // Calculate monthly defaults using country-aware turnover rate
        const annualAccessRequests = countryDefaults.cardholderDatabaseSize * countryDefaults.accessRequestsPerCardholder;
        const monthlyAccessDefault = Math.round(annualAccessRequests / 12);
        
        const annualCredentialRequests = (countryDefaults.cardholderDatabaseSize * countryDefaults.cardReplacementPercentage / 100) + 
                                         (countryDefaults.cardholderDatabaseSize * countryDefaults.staffTurnoverRate / 100);
        const monthlyCredentialDefault = Math.round(annualCredentialRequests / 12);

        return {
          ...countryDefaults,
          // Set monthly defaults for Simple mode
          credentialRequestsPerMonth: monthlyCredentialDefault,
          accessRequestsPerMonth: monthlyAccessDefault,
          // Clear database fields for Simple mode
          cardholderDatabaseSize: 0,
          accessRequestsPerCardholder: 0,
        };
      }

      // Otherwise preserve existing values but adapt to Simple mode
      const countryDefaults = getCountryAwareDefaults(prevInputs.country);
      const annualAccessRequests = countryDefaults.cardholderDatabaseSize * countryDefaults.accessRequestsPerCardholder;
      const monthlyAccessDefault = Math.round(annualAccessRequests / 12);
      
      const annualCredentialRequests = (countryDefaults.cardholderDatabaseSize * countryDefaults.cardReplacementPercentage / 100) + 
                                       (countryDefaults.cardholderDatabaseSize * countryDefaults.staffTurnoverRate / 100);
      const monthlyCredentialDefault = Math.round(annualCredentialRequests / 12);

      return {
        ...prevInputs,
        // Update turnover rate to country-based suggestion for simple mode (use country average)
        staffTurnoverRate: getSuggestedTurnoverRateForSimpleCalculator(prevInputs.country),
        // Set monthly defaults derived from env if currently 0, otherwise preserve user values
        credentialRequestsPerMonth: prevInputs.credentialRequestsPerMonth === 0 ? monthlyCredentialDefault : prevInputs.credentialRequestsPerMonth,
        accessRequestsPerMonth: prevInputs.accessRequestsPerMonth === 0 ? monthlyAccessDefault : prevInputs.accessRequestsPerMonth,
        // Clear database fields only to avoid calculation conflicts (they interfere with Simple mode)
        cardholderDatabaseSize: 0,
        accessRequestsPerCardholder: 0,
      };
    });
  }, []);

  const switchToDetailedMode = useCallback(() => {
    setInputs(prevInputs => {
      // If all inputs are currently empty/zero, load defaults
      const isEmpty = prevInputs.cardholderDatabaseSize === 0 && 
                     prevInputs.costPerEmployee === 0 && 
                     prevInputs.credentialRequestsPerMonth === 0 &&
                     prevInputs.accessRequestsPerMonth === 0;
      
      if (isEmpty) {
        const countryDefaults = getCountryAwareDefaults(prevInputs.country);
        
        return {
          ...countryDefaults,
          // Clear monthly inputs for Detailed mode
          credentialRequestsPerMonth: 0,
          accessRequestsPerMonth: 0,
        };
      }

      // Otherwise preserve existing values but adapt to Detailed mode
      // Keep existing industry (or undefined if none) and adjust turnover rate accordingly
      const currentTurnoverRate = prevInputs.industry ? 
        getExactTurnoverRateForAdvancedCalculator(prevInputs.industry, prevInputs.country) :
        (prevInputs.staffTurnoverRate === 0 ? getCountryAverageTurnoverRate(prevInputs.country) : prevInputs.staffTurnoverRate);
      
      return {
        ...prevInputs,
        // Keep existing industry selection (or undefined)
        // Clear monthly inputs to avoid interference with database calculations (they override database mode)
        credentialRequestsPerMonth: 0,
        accessRequestsPerMonth: 0,
        // Set database defaults from env if currently 0, otherwise preserve user values
        cardholderDatabaseSize: prevInputs.cardholderDatabaseSize === 0 ? DEFAULT_FORM_VALUES.cardholderDatabaseSize : prevInputs.cardholderDatabaseSize,
        accessRequestsPerCardholder: prevInputs.accessRequestsPerCardholder === 0 ? DEFAULT_FORM_VALUES.accessRequestsPerCardholder : prevInputs.accessRequestsPerCardholder,
        // Preserve other database-related fields if they're not set
        cardReplacementPercentage: prevInputs.cardReplacementPercentage === 0 ? DEFAULT_FORM_VALUES.cardReplacementPercentage : prevInputs.cardReplacementPercentage,
        staffTurnoverRate: currentTurnoverRate,
      };
    });
  }, []);

  const loadSharedState = useCallback((shareData: ShareableState, targetWizardStep?: number) => {
    try {      
      // Set resetting flag to prevent auto-calculation during load
      setIsResetting(true);
      setHasUserInteracted(true);
      
      // Load calculator inputs
      setInputs(shareData.inputs);
      
      // Set currency from shared data
      settings.setCurrency(shareData.currency as SupportedCurrency);
      
      // Load pricing configuration if provided
      if (shareData.pricing) {
        settings.setPricing(shareData.pricing);
      }
      
      // Clear existing results to force recalculation with shared data  
      setResults(null);
      
      // Set wizard step to target or default to beginning
      setWizardStep(targetWizardStep ?? 0);
      
      // Clear resetting flag after a short delay to allow state to settle
      setTimeout(() => {
        setIsResetting(false);
      }, 100);
      
    } catch (error) {
      console.error('Failed to load shared state:', error);
      // Reset on error to prevent broken state
      setIsResetting(false);
      throw new Error('Failed to load shared calculation');
    }
  }, [settings]);

  return {
    // State
    inputs,
    pricing,
    results,
    isCalculating,
    currency,
    wizardStep,
    // Actions
    setInputs,
    setPricing: settings.setPricing,
    updateInput,
    updatePricing,
    setCurrency: settings.setCurrency,
    calculate,
    reset,
    loadDefaults,
    resetPricing: settings.resetPricingToDefaults,
    switchToSimpleMode,
    switchToDetailedMode,
    setWizardStep,
    resetWizard,
    loadSharedState,
  };
}
